import React from 'react';
import styles from '../../styles/components/answers/MultipleChoice.module.css';
import UUID from '@ax2/common/model/UUID';
import {
  ParticipantDefinedFieldData,
  ParticipantDefinedField,
} from './ParticipantDefinedOption';
import _ from 'lodash';
import { CheckBox } from '@ax2/common/components/CheckBox';

export interface MultipleChoiceOption {
  id: UUID;
  title: string;
  subtitle?: string;
  isExclusive: boolean;
}
export type MultipleChoiceParticipantDefinedOption = ParticipantDefinedFieldData;

export interface MultipleChoiceData {
  options: (MultipleChoiceOption | MultipleChoiceParticipantDefinedOption)[];
  preselected: UUID[];
  onCompletion: (opts: UUID[]) => void;
  key?: string;
}

interface MultipleChoiceSelectionData {
  id: UUID;
  selected: boolean;
  isExclusive: boolean;
}

function getInitState(
  options: (MultipleChoiceOption | MultipleChoiceParticipantDefinedOption)[],
  preselected: UUID[]
) {
  return _.map(options, d => {
    return {
      id: d.id,
      selected: _.indexOf(preselected, d.id) !== -1,
      isExclusive: 'isExclusive' in d && d.isExclusive,
    };
  });
}
function normalizeData(state: MultipleChoiceSelectionData[]): UUID[] {
  return _.map(_.filter(state, s => s.selected), s => s.id);
}
function getTitleElement(
  opt: MultipleChoiceOption | MultipleChoiceParticipantDefinedOption
) {
  return 'title' in opt ? (
    <span className={styles.selectionText}>{opt.title}</span>
  ) : (
    <div className={styles.selectionInput} onClick={e => e.stopPropagation()}>
      <ParticipantDefinedField {...opt} />
    </div>
  );
}

function getOptionsWithNewSelection(
  data: MultipleChoiceSelectionData[],
  selectionIndex: number
) {
  const option = data[selectionIndex];
  if (option.selected) {
    return getDataWithDeselection(data, selectionIndex);
  } else {
    if (option.isExclusive) {
      return getDataWithExclusiveSelection(data, selectionIndex);
    } else {
      return getDataWithNonExclusiveSelection(data, selectionIndex);
    }
  }
}
function getDataWithDeselection(
  data: MultipleChoiceSelectionData[],
  index: number
) {
  const newData = _.clone(data);
  newData[index].selected = false;
  return newData;
}

function getDataWithExclusiveSelection(
  data: MultipleChoiceSelectionData[],
  index: number
) {
  const newData = _.map(_.clone(data), o => {
    o.selected = false;
    return o;
  });
  newData[index].selected = true;
  return newData;
}
function getDataWithNonExclusiveSelection(
  data: MultipleChoiceSelectionData[],
  index: number
) {
  const newData = _.clone(data);
  const exclusiveOpt = _.find(newData, o => o.isExclusive);
  if (exclusiveOpt) {
    exclusiveOpt.selected = false;
  }
  newData[index].selected = true;
  return newData;
}

export const MultipleChoice: React.FC<MultipleChoiceData> = props => {
  const [options, setOptions] = React.useState<MultipleChoiceSelectionData[]>(
    getInitState(props.options, props.preselected)
  );
  return (
    <div>
      {options.map((option, index) => (
        <div
          key={option.id}
          className={styles.selectionContainer}
          onClick={() => {
            const newOptions = getOptionsWithNewSelection(options, index);
            setOptions(newOptions);
            props.onCompletion(normalizeData(newOptions));
          }}
        >
          <CheckBox hasSelection={option.selected} tabIndex={index + 1} />
          {getTitleElement(props.options[index])}
        </div>
      ))}
    </div>
  );
};
